import { createBoundedUseStore } from 'common-functions';
import { Channels } from 'server-schemas';
import { createStore } from 'zustand';
import { PASSENGER_ID_PAIRINGS } from '../../../apps/funnel/src/shared/const';
import { ClientSideError, ErrorRecovery } from '../error';
import { PassengerIdsPairing } from 'server-schemas/channels/bundle';

export type BasketStoreTypes = {
  basket: Channels.Basket.BasketResponse;
  setBasket: (basket: Channels.Basket.BasketResponse) => void;
  getDlxIdsOfEjPlusPassengersFromStorage: () => string[] | undefined;
  getPassengerIdsPairings: () => PassengerIdsPairing[];
};

const getDlxIdsOfEjPlusPassengersFromStorage = (): string[] | undefined => {
  const passengersInBasket = BasketStore.getState().basket.passengers;

  if (!passengersInBasket?.length) {
    throw new ClientSideError({
      message: 'No passengers in basket store',
      recovery: ErrorRecovery.EXIT_ERROR_PAGE,
      showCorrelation: true
    });
  }

  const ejPlusPassengerIds: string[] = [];
  passengersInBasket.forEach(passenger => {
    if (passenger.EjPlusNumber && passenger.EjPlusNumber !== '') {
      ejPlusPassengerIds.push(passenger.InternalId);
    }
  });
  if (!ejPlusPassengerIds.length) return undefined;

  const dlxIdsPairings: PassengerIdsPairing[] = getPassengerIdsPairings();

  const ejPlusPassengerDlxIds: string[] = [];
  ejPlusPassengerIds.forEach(internalId => {
    const pairing = dlxIdsPairings.find(pair => pair.internalId.toLowerCase() === internalId.toLowerCase());
    if (pairing?.dlxId) {
      ejPlusPassengerDlxIds.push(pairing.dlxId);
    }
  });

  if (ejPlusPassengerDlxIds.length <= 0) {
    throw new ClientSideError({
      message: 'Error retrieving DLX IDs for EJ Plus passenger from session storage',
      recovery: ErrorRecovery.EXIT_ERROR_PAGE,
      showCorrelation: true
    });
  }

  return ejPlusPassengerDlxIds;
};

const getPassengerIdsPairings = (): PassengerIdsPairing[] => {
  const passengerIdsPairings = sessionStorage.getItem(PASSENGER_ID_PAIRINGS);

  if (!passengerIdsPairings?.length || passengerIdsPairings === '[]') {
    throw new ClientSideError({
      message: 'No passenger pairings in session storage',
      recovery: ErrorRecovery.EXIT_ERROR_PAGE,
      showCorrelation: true
    });
  }

  const parsedIdsPairings = JSON.parse(passengerIdsPairings);

  return parsedIdsPairings.map((pairing: string[][]) => ({ internalId: pairing[0], dlxId: pairing[1] }));
};

export const BasketStore = createStore<BasketStoreTypes>(set => ({
  basket: {} as Channels.Basket.BasketResponse,
  setBasket: basket => set(state => ({ ...state, basket })),
  getDlxIdsOfEjPlusPassengersFromStorage: getDlxIdsOfEjPlusPassengersFromStorage,
  getPassengerIdsPairings: getPassengerIdsPairings
}));

export const useBasketStore = createBoundedUseStore(BasketStore);
